import { Search, X } from "lucide-react";
import { cloneElement, forwardRef, isValidElement, useId, type InputHTMLAttributes, type ReactElement, type ReactNode, type TextareaHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

const field =
  "w-full rounded-xl border border-line bg-surface text-[14px] text-ink placeholder:text-ink-3 outline-none transition focus:border-accent focus:shadow-[var(--ring)] disabled:opacity-50 aria-[invalid=true]:border-bad aria-[invalid=true]:focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--bad)_22%,transparent)]";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { leading?: ReactNode; trailing?: ReactNode; inputSize?: "sm" | "md" | "lg" }>(function Input(
  { className, leading, trailing, inputSize = "md", ...props },
  ref,
) {
  const h = inputSize === "sm" ? "h-8 text-[13px]" : inputSize === "lg" ? "h-12 text-[15px]" : "h-10";
  if (!leading && !trailing) return <input ref={ref} className={cn(field, h, "px-3.5", className)} {...props} />;
  return (
    <div className={cn("relative", className)}>
      {leading && <span className="pointer-events-none absolute inset-y-0 start-3 flex items-center text-ink-3 [&_svg]:size-4">{leading}</span>}
      <input ref={ref} className={cn(field, h, leading ? "ps-9" : "ps-3.5", trailing ? "pe-10" : "pe-3.5")} {...props} />
      {trailing && <span className="absolute inset-y-0 end-2 flex items-center">{trailing}</span>}
    </div>
  );
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...props }, ref) {
  return <textarea ref={ref} className={cn(field, "min-h-[88px] resize-y px-3.5 py-2.5 leading-relaxed", className)} {...props} />;
});

export function SearchInput({
  value,
  onChange,
  placeholder,
  className,
  inputSize = "md",
  label,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "value"> & { value: string; onChange: (v: string) => void; inputSize?: "sm" | "md" | "lg"; label?: string }) {
  return (
    <Input
      type="search"
      role="searchbox"
      aria-label={label ?? placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      inputSize={inputSize}
      leading={<Search />}
      trailing={
        value ? (
          <button type="button" onClick={() => onChange("")} className="grid size-6 place-items-center rounded-md text-ink-3 hover:bg-surface-2 hover:text-ink" aria-label="Clear">
            <X className="size-3.5" />
          </button>
        ) : undefined
      }
      className={className}
      {...rest}
    />
  );
}

/**
 * A labelled field. The label, hint and error are wired to the control with
 * htmlFor / aria-describedby / aria-invalid, so screen readers announce them.
 * Use `group` for controls that aren't a single input (radio cards, segmented).
 */
export function Field({
  label,
  hint,
  error,
  children,
  group,
  required,
  className,
  action,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  children: ReactNode;
  group?: boolean;
  required?: boolean;
  className?: string;
  action?: ReactNode;
}) {
  const id = useId();
  const controlId = `${id}-c`;
  const hintId = `${id}-h`;
  const labelId = `${id}-l`;
  const describedBy = error || hint ? hintId : undefined;
  const control =
    !group && isValidElement(children)
      ? cloneElement(children as ReactElement<Record<string, unknown>>, {
          id: (children.props as { id?: string }).id ?? controlId,
          "aria-describedby": describedBy,
          "aria-invalid": error ? true : undefined,
          "aria-required": required || undefined,
        })
      : children;
  return (
    <div className={cn("space-y-1.5", className)} role={group ? "group" : undefined} aria-labelledby={group ? labelId : undefined}>
      <div className="flex items-center gap-2">
        {group ? (
          <span id={labelId} className="text-[13px] font-semibold text-ink">
            {label}
            {required && <span className="ms-0.5 text-bad">*</span>}
          </span>
        ) : (
          <label id={labelId} htmlFor={controlId} className="text-[13px] font-semibold text-ink">
            {label}
            {required && <span className="ms-0.5 text-bad">*</span>}
          </label>
        )}
        {action && <div className="ms-auto">{action}</div>}
      </div>
      {control}
      {(error || hint) && (
        <p id={hintId} className={cn("text-[12px] leading-relaxed", error ? "text-bad" : "text-ink-3")} role={error ? "alert" : undefined}>
          {error || hint}
        </p>
      )}
    </div>
  );
}
